import React, { useState, useEffect } from 'react';
import { useStore } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { TimePicker, Segmented, Button, PageHeader, pageWrapCls, inputCls } from '../components/ui';
import { useAuth } from '../auth/AuthContext';
import { useTheme, THEME_OPTIONS } from '../lib/theme';
import { IconSun, IconMoon, IconMonitor } from '../components/icons';
import RateTableManager from './settings/RateTableManager';
import MaterialsManager from './settings/MaterialsManager';
import UserRolesManager from './settings/UserRolesManager';
import StatusManager from './settings/StatusManager';
import DataMigration from './settings/DataMigration';
import StorageSettingsManager from './settings/StorageSettingsManager';

function SettingsPage() {
    const { config, saveConfig } = useStore();
    const { saveThemePref } = useAuth();
    const { pref: themePref, isDark } = useTheme();
    const [settingsTab, setSettingsTab] = useState('general');
    const [form, setForm] = useState({ company_name: config.company_name || '', phone: config.phone || '', address: config.address || '', default_hourly_rate: config.default_hourly_rate || 225, sms_reminder_time: config.sms_reminder_time || '19:00' });
    const { show: showToast } = useToast();
    const [saving, setSaving] = useState(false);
    // Sentry smoke-test toggle. Hidden unless URL contains sentry-test=1 — keeps it
    // available forever for re-verification but invisible to crews.
    // Match either before the hash (/?sentry-test=1#...) or after it
    // (/#...?sentry-test=1) since hash-routed URLs make the search-vs-hash
    // distinction easy to fumble.
    const [crashOnRender, setCrashOnRender] = useState(false);
    const showSentryTest = typeof window !== 'undefined' && /[?&]sentry-test=1\b/.test(window.location.href);
    if (crashOnRender) {
        throw new Error('BBM Sentry smoke test — please ignore (triggered from Settings)');
    }
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const save = async () => { setSaving(true); try { for (const [k, v] of Object.entries(form)) { await saveConfig(k, v); } showToast('Settings saved'); } catch(e) { showToast('Error: ' + e.message, 'error'); } finally { setSaving(false); } };
    useEffect(() => { setForm({ company_name: config.company_name || '', phone: config.phone || '', address: config.address || '', default_hourly_rate: config.default_hourly_rate || 225, sms_reminder_time: config.sms_reminder_time || '19:00' }); }, [config]);

    const tabs = [
        { id: 'general', label: 'General' },
        { id: 'appearance', label: 'Appearance' },
        { id: 'users', label: 'Users & Roles' },
        { id: 'storage', label: 'Storage' },
    ];

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Settings"
                summary={tabs.map(t => t.label).join(' · ')}
                help={<>
                    <p>Company-wide setup lives here. General holds the company details, rate table, materials, SMS reminder time and the status list; Users &amp; Roles controls who can see what; Storage sets up facilities and unit pricing.</p>
                    <p className="mt-2">Appearance is per person — it only changes your own light / dark preference. Save Settings on the General tab writes every field at once.</p>
                </>}
            />

            <Segmented
                ariaLabel="Settings section"
                size="md"
                items={tabs.map(tab => ({ value: tab.id, label: tab.label }))}
                value={settingsTab}
                onChange={setSettingsTab}
            />

            {settingsTab === 'general' && (
                <>
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h2 className="text-lg font-bold text-slate-900 mb-4">Company Information</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-600">Company Name</label>
                                <input className={inputCls} value={form.company_name} onChange={e => set('company_name', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-600">Phone</label>
                                    <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-600">Address</label>
                                    <input className={inputCls} value={form.address} onChange={e => set('address', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <RateTableManager />
                    <MaterialsManager />
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <h2 className="text-lg font-bold text-slate-900 mb-4">SMS Notifications</h2>
                        <div>
                            <label className="text-xs font-bold text-slate-600 block mb-1">Reminder Time</label>
                            <TimePicker value={form.sms_reminder_time} onChange={v => set('sms_reminder_time', v)} ariaLabel="SMS reminder time" className="w-full max-w-[220px]" />
                        </div>
                    </div>
                    <Button variant="primary" onClick={save} loading={saving}>Save Settings</Button>

                    <StatusManager />

                    <DataMigration />
                </>
            )}

            {/* Appearance (theme.md, 2026-09-11): per-user light / dark /
                system. Saved to user_profiles.theme_pref (sql/091) and
                mirrored on this device so it applies before login. */}
            {settingsTab === 'appearance' && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
                    <h2 className="text-lg font-bold text-slate-900 mb-1">Appearance</h2>
                    <p className="text-sm text-slate-500 mb-4">Just for you — everyone picks their own. <span className="font-semibold">System</span> follows your Mac or phone, so it can switch at sunset if the device does.</p>
                    <Segmented
                        ariaLabel="Theme"
                        items={THEME_OPTIONS.map(o => ({ ...o, icon: o.value === 'light' ? <IconSun size={15} /> : o.value === 'dark' ? <IconMoon size={15} /> : <IconMonitor size={15} /> }))}
                        value={themePref}
                        onChange={v => saveThemePref(v)}
                    />
                    <p className="text-xs text-slate-400 mt-3">Currently showing: {isDark ? 'dark' : 'light'}. The sun / moon button under your name in the sidebar is the quick switch.</p>
                </div>
            )}

            {settingsTab === 'users' && <UserRolesManager />}

            {/* Storage v2 (sql/086, 2026-09-08): facilities, unit inventory,
                size price list, owner-only gate code. */}
            {settingsTab === 'storage' && <StorageSettingsManager />}

            {showSentryTest && (
                <div className="mt-8 p-4 border border-amber-300 bg-amber-50 rounded-lg">
                    <div className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1">Sentry smoke test</div>
                    <div className="text-xs text-amber-800 mb-2">Clicking this will crash the page on purpose so we can verify Sentry is capturing errors. Reload after.</div>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setCrashOnRender(true)}
                    >
                        Trigger test error
                    </Button>
                </div>
            )}

        </div>
    );
}

export default SettingsPage;
